import {
    CorrelationIdGenerator,
    defaultSelectId,
    ENTITY_CACHE_SELECTOR_TOKEN,
    EntityActionFactory,
    EntityActionOptions,
    EntityCache,
    EntityCacheSelector,
    EntityDispatcher,
    EntityDispatcherBase,
    EntityDispatcherDefaultOptions,
    EntityOp,
    IdSelector,
    QueryParams
} from 'ngrx-data';

import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Action, ScannedActionsSubject, Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { map, shareReplay, withLatestFrom } from 'rxjs/operators';
import { ResponseMapper } from '@kaiser-software/chassis-resource/src/utils/response-mapper';

export class ChassisEntityDispatcherBase<T> extends EntityDispatcherBase<T> {

    /**
     * Dispatch action to query remote storage for all entities and
     * merge the queried entities into the cached collection.
     * @returns A terminating Observable of the queried entities that are in the collection
     * after server reports success query or the query error.
     * @see load()
     */
    getAll(options?: EntityActionOptions): Observable<T[]> {
        options      = this['setQueryEntityActionOptions'](options);
        const action = this.createEntityAction(EntityOp.QUERY_ALL, null, options);
        this.dispatch(action);
        return this['getResponseData$']<T[]>(options.correlationId).pipe(
            // Use the returned entity ids to get the entities from the collection
            // as they might be different from the entities returned from the server
            // because of unsaved changes (deletes or updates).
            withLatestFrom(this['entityCollection$']),
            map(([entities, collection]) => this.mapResponse(entities, collection)),
            shareReplay(1)
        );
    }

    /**
     * Dispatch action to query remote storage for the entities that satisfy a query expressed
     * with either a query parameter map or an HTTP URL query string,
     * and merge the results into the cached collection.
     * @params queryParams the query in a form understood by the server
     * @returns A terminating Observable of the queried entities
     * after server reports successful query or the query error.
     */
    getWithQuery(queryParams: QueryParams | string, options?: EntityActionOptions): Observable<T[]> {
        options      = this['setQueryEntityActionOptions'](options);
        const action = this.createEntityAction(EntityOp.QUERY_MANY, queryParams, options);
        this.dispatch(action);
        return this['getResponseData$']<T[]>(options.correlationId).pipe(
            // Use the returned entity ids to get the entities from the collection
            // as they might be different from the entities returned from the server
            // because of unsaved changes (deletes or updates).
            withLatestFrom(this['entityCollection$']),
            map(([entities, collection]) => this.mapResponse(entities, collection)),
            shareReplay(1)
        );
    }

    /**
     * Map the collection response with pagination
     *
     * @param entities
     * @param collection
     */
    protected mapResponse(entities: any, collection: any) {
        return {
            total: entities.total,
            current_page: entities.current_page,
            per_page: entities.per_page,
            entities: ResponseMapper.createFromCollectionResponse(entities).reduce(
                (acc, e: any) => {
                    const entity = collection.entities[this.selectId(e)];
                    if (entity) {
                        acc.push(entity); // only return an entity found in the collection
                    }
                    return acc;
                },
                [] as T[]
            )
        };
    }
}

/** Creates EntityDispatchers for entity collections */
@Injectable()
export class ChassisEntityDispatcherFactory implements OnDestroy {
    /**
     * Actions scanned by the store after it processed them with reducers.
     * A replay observable of the most recent action reduced by the store.
     */
    reducedActions$: Observable<Action>;
    private raSubscription: Subscription;

    constructor(
        private entityActionFactory: EntityActionFactory,
        private store: Store<EntityCache>,
        private entityDispatcherDefaultOptions: EntityDispatcherDefaultOptions,
        @Inject(ScannedActionsSubject) scannedActions$: Observable<Action>,
        @Inject(ENTITY_CACHE_SELECTOR_TOKEN) private entityCacheSelector: EntityCacheSelector,
        private correlationIdGenerator: CorrelationIdGenerator
    ) {
        // Replay because sometimes in tests will fake data service with synchronous observable
        // which makes subscriber miss the dispatched actions.
        // Of course that's a testing mistake. But easy to forget, leading to painful debugging.
        this.reducedActions$ = scannedActions$.pipe(shareReplay(1));
        // Start listening so late subscriber won't miss the most recent action.
        this.raSubscription  = this.reducedActions$.subscribe();
    }

    /**
     * Create an `EntityDispatcher` for an entity type `T` and store.
     */
    create<T>(
        /** Name of the entity type */
        entityName: string,
        /**
         * Function that returns the primary key for an entity `T`.
         * Usually acquired from `EntityDefinition` metadata.
         */
        selectId: IdSelector<T>                                 = defaultSelectId,
        /** Defaults for options that influence dispatcher behavior such as whether
         * `add()` is optimistic or pessimistic;
         */
        defaultOptions: Partial<EntityDispatcherDefaultOptions> = {}
    ): EntityDispatcher<T> {
        // merge w/ defaultOptions with injected defaults
        const options: EntityDispatcherDefaultOptions = {...this.entityDispatcherDefaultOptions, ...defaultOptions};
        return new ChassisEntityDispatcherBase<T>(
            entityName,
            this.entityActionFactory,
            this.store,
            selectId,
            options,
            this.reducedActions$,
            this.entityCacheSelector,
            this.correlationIdGenerator
        );
    }

    ngOnDestroy() {
        this.raSubscription.unsubscribe();
    }
}



// WEBPACK FOOTER //
// ./libs/chassis-resource/src/services/chassis-entity-dispatcher-base.ts